import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CurrentLang } from 'utils/lang';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { loadSearchWidgetScript } from 'utils/searchWidget';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useHybridSearchEnabled from 'hooks/useHybridSearchEnabled';
import { usePersonalizationFeatures } from 'utils/personalizationAbTest';
import parseSearchUrl from 'utils/search/parseSearchUrl';
import { isValidSearchDate } from 'utils/search/searchDateParam';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import ChildInArmsInfoModal from './ChildInArmsInfoModal';

/**
 * Search widget component. Implements the search widget from reservamos.mx
 * @param {Object} props - Component props
 * @param {string} props.origin - Origin city slug
 * @param {string} props.destination - Destination city slug
 * @param {string} props.departureDate - Departure date
 * @param {string} props.returnDate - Return date
 * @param {string} props.hybridTrip - Hybrid trip. Could be departure, return or both
 * @param {function} props.searchCallback - Callback to be called when the search button is clicked
 * @param {function} props.onSearch - Callback to handle search event. Receives absoluteSearchUrl. If provided, overrides default redirect behavior.
 * @param {Object} props.additionalProps - Additional data-prop-* attributes to pass to the widget
 * @returns {JSX} - React component
 */
const SearchWidget = ({
  origin,
  destination,
  departureDate,
  returnDate,
  hybridTrip,
  searchCallback,
  onSearch,
  additionalProps = {},
}) => {
  const { search, whitelabelConfig } = useSelector((state) => state);
  const { env, features } = whitelabelConfig;
  const searchPassengersEnabled = features.SEARCH_PASSENGERS;
  const displayTypeAB = useGrowthBookFeatureValue('search_widget_display_type');
  const hybridSearchEnabled = useHybridSearchEnabled();
  const history = useHistory();
  const { t } = useTranslation('general');
  const departure = search?.get('departure');
  const returns = search?.get('return');
  const passengers = search?.get('passengers')?.toJS() || {};
  const widgetSelector = useRef(uuidv4());
  const widgetRef = useRef(null);
  const [isChildInArmsInfoOpen, setIsChildInArmsInfoOpen] = useState(false);
  /**
   * Close handler passed to the child-in-arms info modal so it can flip the
   * `isChildInArmsInfoOpen` state back to false on dismissal.
   */
  const closeChildInArmsInfo = () => setIsChildInArmsInfoOpen(false);

  // Check if search history should be enabled based on the personalization feature flag
  const searchHistoryEnabled = usePersonalizationFeatures();

  const widgetProps = env.searchWidget;
  const lang = CurrentLang();

  const passengersOptionsList = (() => {
    const raw = widgetProps.passengersOptions;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((x) => `${x}`.trim()).filter(Boolean);

    return `${raw}`
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  })();

  let decodeSearchPassengers = '';
  Object.keys(passengers).forEach((key) => {
    if (passengers[key] && passengersOptionsList.includes(key)) {
      decodeSearchPassengers += `${key}=${passengers[key]},`;
    }
  });
  const withInitialData = [origin, destination, departureDate, returnDate, hybridTrip].some(
    (value) => value !== undefined,
  );

  // Initial data to be used in the search widget
  const initialData = {
    'data-prop-origin': origin,
    'data-prop-destination': destination,
    'data-prop-departure-date': departure?.get('isOpen') ? t('open_ticket') : departureDate,
    'data-prop-hybrid-trip': hybridTrip,
  };

  if (returnDate) {
    initialData['data-prop-return-date'] = returnDate;
  }

  if (returns?.get('isOpen')) {
    initialData['data-prop-return-date'] = t('open_ticket');
  }

  // Creates the search widget with the current selector
  const createSearchWidget = () => {
    widgetRef.current = window.createSearchWidget({
      selector: `[data-widget-host="${widgetSelector.current}"]`,
      clean: true,
    });
  };

  useEffect(() => {
    let script;
    const { useDynamicLoad } = env.searchWidget;
    if (useDynamicLoad) {
      // If the search widget is already loaded, create it
      if (window.createSearchWidget) {
        createSearchWidget();
      } else {
        // If the search widget is not loaded, wait for it to be loaded and create it
        window.addEventListener('searchWidgetReady', createSearchWidget);
      }
    } else {
      script = loadSearchWidgetScript(env.searchWidget.version);
    }

    return () => {
      if (useDynamicLoad) {
        // Destroy the search widget when the component is unmounted
        widgetRef.current?.destroy();
        // Remove the listener for the search widget
        window.removeEventListener('searchWidgetReady', createSearchWidget);
      } else {
        document.body.removeChild(script);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configFeaturesFlags = () => {
    const { featuresFlags } = widgetProps;
    if (!featuresFlags) return widgetProps;
    const newWidgetProps = { ...widgetProps };
    if (featuresFlags.showDepartureDatePickerText)
      newWidgetProps.departureDatePickerText = t('text.departureCalendarText');
    if (featuresFlags.showReturnDatePickerText)
      newWidgetProps.returnDatePickerText = t('text.returnCalendarText');
    return newWidgetProps;
  };
  /**
   * Transform props to be used in the search widget
   * Props are data-prop-* attributes in the div element
   * Values from config.searchWidget are camelCase and should be converted to kebab-case
   * @returns {object} - Transformed props
   */
  const transformProps = () => {
    const widgetWithExtras = configFeaturesFlags();
    const transformedProps = {};
    Object.keys(widgetWithExtras).forEach((key) => {
      const value = widgetWithExtras[key];
      const transformedKey = key.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`);
      transformedProps[`data-prop-${transformedKey}`] = value;
    });

    // If personalization is disabled, override the max-recent-searches prop to show 0 results
    // Must be 0 because if there is no value, the widget will show 3 results by default
    if (!searchHistoryEnabled) {
      transformedProps['data-prop-max-recent-searches'] = '0';
    }

    // Apply display type from feature flag if available
    // Valid values: city, state, city_state, only_terminal, terminal_city
    if (displayTypeAB && features.SEARCH_WIDGET_AB_TEST_ENABLED) {
      transformedProps['data-prop-display-type'] = displayTypeAB;
    }

    // Apply passengers dropdown from feature flag
    transformedProps['data-prop-passengers-dropdown'] = Boolean(searchPassengersEnabled);

    // Pass the resolved value so Funnel and the embedded widget use the same feature state.
    transformedProps['data-prop-hybrid-search-enabled'] = hybridSearchEnabled;

    // Pass the raw tenant base place type; the widget validates it and ignores it when hybrid is on.
    const defaultPlaceType = features.SEARCH_DEFAULT_PLACE_TYPE;
    if (typeof defaultPlaceType === 'string' && defaultPlaceType !== '') {
      transformedProps['data-prop-default-place-type'] = defaultPlaceType;
    }

    // Merge additional props passed from parent component (allows overriding any prop above)
    Object.assign(transformedProps, additionalProps);

    return transformedProps;
  };

  const transformedProps = transformProps();

  /**
   * Listen to search event from the search widget and redirect to the search page
   * This hook sets the listener and removes it when the component is unmounted
   */
  useEffect(() => {
    const searchEventListener = (event) => {
      const { absoluteSearchUrl } = event.detail;

      // If onSearch callback exists, use it instead of default behavior
      if (onSearch) {
        onSearch(absoluteSearchUrl);
        if (searchCallback) searchCallback(absoluteSearchUrl);
        return;
      }

      // Default behavior: redirect
      let pathname;
      let search;
      try {
        // A base is supplied so a relative absoluteSearchUrl (unexpected, but not
        // impossible) still parses instead of throwing — it needs to reach the same
        // date-validity check below as a normal absolute widget URL, not skip it via the
        // catch block. When absoluteSearchUrl is already absolute, the base is ignored.
        ({ pathname, search } = new URL(absoluteSearchUrl, window.location.origin));
      } catch (error) {
        // Only a truly malformed value (not just relative) reaches here now. Report it and
        // still try to navigate — better than silently doing nothing on the user's click.
        errorMonitoring.notify({ error, info: { absoluteSearchUrl } });
        history.push(absoluteSearchUrl);
        if (searchCallback) searchCallback(absoluteSearchUrl);
        return;
      }

      const parsedUrl = parseSearchUrl(pathname, search);
      const hasInvalidDate =
        parsedUrl &&
        (!isValidSearchDate(parsedUrl.departureDate) ||
          (parsedUrl.returnDate && !isValidSearchDate(parsedUrl.returnDate)));

      if (hasInvalidDate) {
        // The search widget occasionally produces an unparseable date (e.g. "Invalid Date")
        // for its recent-searches / preferred-terminal flows; redirect to the origin/
        // destination page (DynamicSearch) instead of navigating to a broken search-results
        // URL. Only take this branch when the URL parsed as a recognized search-results shape
        // — a URL that doesn't (e.g. an open-ticket or origin/destination-only route) isn't
        // necessarily broken, just outside what parseSearchUrl models, so it falls through to
        // normal navigation. `replace`, not `push`: the broken URL shouldn't linger in history
        // for the back button. searchCallback is deliberately not called here — no search
        // happened. `search` is preserved, same as every other date-validity redirect in this
        // change (ValidatedSearchProcessor, ValidatedOpenTicketSearch, DynamicSearch) — a
        // coupon or utm_* param on the widget's URL shouldn't be dropped just because its date
        // segment was broken.
        history.replace({
          pathname: `/search/${parsedUrl.originSlug}/${parsedUrl.destinationSlug}`,
          search,
        });
        return;
      }

      history.push(pathname + search);
      if (searchCallback) searchCallback(absoluteSearchUrl);
    };
    // The event is removed and added again to avoid multiple listeners
    window.removeEventListener('SearchWidget:search', searchEventListener);
    window.addEventListener('SearchWidget:search', searchEventListener);

    return () => {
      window.removeEventListener('SearchWidget:search', searchEventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the funnel-owned info modal when the widget dispatches the click event
  // on the `Criança de colo` link. Widget closes its picker before dispatching;
  // the modal's own dismiss handler dispatches `searchwidget:passenger-info-dismissed`
  // so the widget reopens the picker with prior selections.
  useEffect(() => {
    const handlePassengerInfoClick = (event) => {
      if (event.detail?.infoModalKey === 'child_in_arms_info') {
        setIsChildInArmsInfoOpen(true);
      }
    };
    document.addEventListener('searchwidget:passenger-info-click', handlePassengerInfoClick);
    return () => {
      document.removeEventListener('searchwidget:passenger-info-click', handlePassengerInfoClick);
    };
  }, []);

  const { useDynamicLoad } = env.searchWidget;

  return (
    <>
      <div
        className="searchbar"
        data-widget-host={useDynamicLoad ? widgetSelector.current : 'habitat'}
        data-prop-lang={lang}
        data-prop-compact-height=""
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...transformedProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(withInitialData && initialData)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        data-prop-trigger-search-event
        data-prop-geolocation-origin="false"
        data-prop-passengers-values={decodeSearchPassengers}
      />
      <ChildInArmsInfoModal isOpen={isChildInArmsInfoOpen} onClose={closeChildInArmsInfo} />
    </>
  );
};

SearchWidget.propTypes = {
  origin: PropTypes.string,
  destination: PropTypes.string,
  departureDate: PropTypes.string,
  returnDate: PropTypes.string,
  hybridTrip: PropTypes.string,
  searchCallback: PropTypes.func,
  onSearch: PropTypes.func,
  additionalProps: PropTypes.object,
};

export default SearchWidget;
